import { FirebaseOrder } from "@/models";

/**
 * Export orders data to CSV format
 */
export const exportOrdersToCsv = (orders: FirebaseOrder[]) => {
  const headers = [
    "ID",
    "Date",
    "Client",
    "Email",
    "Téléphone",
    "Statut",
    "Produits",
    "Montant Total",
    "Adresse",
    "Code Postal",
    "Ville",
    "Méthode de livraison",
    "Statut de préparation",
  ];

  const csvData = orders.map((order) => {
    const orderDate = order.createdAt?.toDate
      ? order.createdAt.toDate().toLocaleDateString("fr-FR")
      : new Date(order.createdAt).toLocaleDateString("fr-FR");

    const totalAmount =
      order.products.reduce(
        (sum, product) => sum + parseFloat(product.amount || "0"),
        0,
      ) + (order.deliveryInfo.shippingCost || 0);

    const products = order.products
      .map((p) => `${p.productReference} (${p.amount}€)`)
      .join("; ");

    return [
      order.id || "",
      orderDate,
      `${order.orderInfo.firstName} ${order.orderInfo.lastName}`,
      order.orderInfo.email,
      order.orderInfo.phone || "",
      order.status,
      products,
      totalAmount.toFixed(2) + "€",
      order.deliveryInfo.address || "",
      order.deliveryInfo.postalCode || "",
      order.deliveryInfo.city || "",
      order.deliveryInfo.deliveryMethod || "",
      order.preparationStatus || "todo",
    ];
  });

  // Create CSV content
  const csvContent = [headers, ...csvData]
    .map((row) => row.map((field) => `"${field}"`).join(","))
    .join("\n");

  // Download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `commandes_${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export shipping labels data
 */
export const exportShippingLabels = (orders: FirebaseOrder[]) => {
  const paidOrders = orders.filter(
    (order) => order.status === "paid" && order.deliveryInfo.address,
  );

  const labelsData = paidOrders.map((order) => ({
    orderId: order.id,
    customerName: `${order.orderInfo.firstName} ${order.orderInfo.lastName}`,
    address: order.deliveryInfo.address,
    postalCode: order.deliveryInfo.postalCode,
    city: order.deliveryInfo.city,
    country: order.deliveryInfo.country || "FR",
    phone: order.orderInfo.phone,
    deliveryMethod: order.deliveryInfo.deliveryMethod,
    pickupPoint: order.deliveryInfo.selectedPickupPoint?.name || "",
  }));

  const headers = [
    "ID Commande",
    "Nom Client",
    "Adresse",
    "Code Postal",
    "Ville",
    "Pays",
    "Téléphone",
    "Méthode de livraison",
    "Point relais",
  ];

  const csvData = labelsData.map((label) => [
    label.orderId,
    label.customerName,
    label.address,
    label.postalCode,
    label.city,
    label.country,
    label.phone,
    label.deliveryMethod,
    label.pickupPoint,
  ]);

  // Create CSV content
  const csvContent = [headers, ...csvData]
    .map((row) => row.map((field) => `"${field}"`).join(","))
    .join("\n");

  // Download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `etiquettes_${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
interface CsvOrderData {
  id: string;
  date: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  reference_produit: string;
  montant: string;
  statut: string;
  statut_preparation: string;
  methode_livraison: string;
  adresse: string;
  ville: string;
  code_postal: string;
  numero_suivi?: string;
}

const formatDate = (timestamp: any): string => {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("fr-FR");
};

export const convertOrderToCsvData = (order: FirebaseOrder): CsvOrderData => {
  // Fonction pour convertir le statut de préparation en texte lisible
  const getPreparationStatusText = (
    status?: "todo" | "in_progress" | "ready",
  ): string => {
    switch (status) {
      case "todo":
        return "À faire";
      case "in_progress":
        return "En cours";
      case "ready":
        return "Prêt";
      default:
        return order.status === "paid" ? "À faire" : "N/A"; // null/undefined considéré comme 'À faire' pour les commandes payées
    }
  };

  return {
    id: order.id || "",
    date: order.createdAt ? formatDate(order.createdAt) : "",
    nom: order.orderInfo.lastName,
    prenom: order.orderInfo.firstName,
    email: order.orderInfo.email,
    telephone: order.orderInfo.phone,
    reference_produit: order.products[0]?.reference || "",
    montant: order.products[0]?.amount || "",
    statut: order.status,
    statut_preparation: getPreparationStatusText(order.preparationStatus),
    methode_livraison: order.deliveryInfo.methodName || order.deliveryInfo.methodId?.toString() || "",
    adresse: order.deliveryInfo.address || "",
    ville: order.deliveryInfo.city || "",
    code_postal: order.deliveryInfo.postalCode || "",
    numero_suivi: order.deliveryInfo?.trackingNumber || "",
  };
};
